/**
 * Activité par heure et jour de semaine.
 *
 * Heatmap et non graphique : la question posée est « quand les gens jouent-ils »,
 * donc une magnitude sur deux dimensions. Sept courbes de 24 points seraient
 * illisibles, et un total par heure écraserait la différence semaine/week-end.
 *
 * ── L'échelle est LINÉAIRE, calée sur l'étendue observée ─────────────────────
 * Deux essais ont été écartés au rendu, ce qui vaut d'être noté :
 *   · par QUANTILES : six paquets de 28 cases chacun donnaient un aplat
 *     uniforme, l'œil ne trouvait plus les heures de pointe ;
 *   · linéaire de ZÉRO au maximum : aucune heure de la semaine n'est proche de
 *     zéro, donc les deux premiers pas ne servaient jamais et tout se tassait
 *     au milieu de la rampe.
 *
 * L'échelle va donc du minimum au maximum observés, ce qui déplie la rampe sur
 * l'étendue réelle. Conséquence à connaître : le pas le plus sombre ne veut pas
 * dire « aucune activité » mais « le creux de la semaine ». Les cases vraiment
 * vides ont leur propre couleur, et le tableau donne les valeurs exactes.
 */

import { Fragment, useMemo } from 'react';

import { WEEKDAY_LABELS } from '@shared/types/stats';

import { formatInteger } from '../lib/format';
import { HEATMAP_EMPTY, SEQUENTIAL_RAMP } from '../lib/theme';
import type { DataColumn, HeatmapCell } from '../types';
import { Card } from './Card';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

interface Row {
  weekday: number;
  label: string;
  total: number;
  peakHour: number | null;
  peakActions: number;
}

const columns: ReadonlyArray<DataColumn<Row>> = [
  { key: 'label', header: 'Jour', render: (row) => row.label },
  { key: 'total', header: 'Actions', numeric: true, render: (row) => formatInteger(row.total) },
  {
    key: 'peak',
    header: 'Heure de pointe',
    render: (row) => (row.peakHour === null ? '—' : `${String(row.peakHour).padStart(2, '0')} h`),
  },
  {
    key: 'peakActions',
    header: 'Actions à la pointe',
    numeric: true,
    render: (row) => formatInteger(row.peakActions),
  },
];

/** Seuils des six pas : découpage linéaire entre le minimum et le maximum observés. */
function buildThresholds(min: number, max: number): number[] {
  if (max <= 0 || max === min) return [];
  const steps = SEQUENTIAL_RAMP.length;
  return Array.from({ length: steps - 1 }, (_, i) => min + ((max - min) * (i + 1)) / steps);
}

export function ActivityHeatmap({ data }: { data: readonly HeatmapCell[] }) {
  const { grid, thresholds, rows, min, max } = useMemo(() => {
    // L'API ne renvoie que les cases non vides : on reconstitue la grille
    // complete, une case absente valant zero.
    const byKey = new Map(data.map((cell) => [`${cell.weekday}-${cell.hour}`, cell.actions]));
    const values = data.map((cell) => cell.actions).filter((v) => v > 0);

    const rows: Row[] = WEEKDAYS.map((weekday) => {
      let total = 0;
      let peakHour: number | null = null;
      let peakActions = 0;
      for (const hour of HOURS) {
        const actions = byKey.get(`${weekday}-${hour}`) ?? 0;
        total += actions;
        if (actions > peakActions) {
          peakActions = actions;
          peakHour = hour;
        }
      }
      return {
        weekday,
        label: WEEKDAY_LABELS[weekday] ?? String(weekday),
        total,
        peakHour,
        peakActions,
      };
    });

    const max = values.length > 0 ? Math.max(...values) : 0;
    const min = values.length > 0 ? Math.min(...values) : 0;
    return { grid: byKey, thresholds: buildThresholds(min, max), rows, min, max };
  }, [data]);

  if (data.length === 0) {
    return (
      <Card title="Activité par heure et jour" className="chart-grid__wide">
        <p className="empty" style={{ height: 120 }}>
          Aucune action sur cette période.
        </p>
      </Card>
    );
  }

  const colorFor = (actions: number): string => {
    if (actions <= 0) return HEATMAP_EMPTY;
    let step = 0;
    while (step < thresholds.length && actions > (thresholds[step] ?? 0)) step += 1;
    return SEQUENTIAL_RAMP[Math.min(step, SEQUENTIAL_RAMP.length - 1)] ?? HEATMAP_EMPTY;
  };

  return (
    <Card
      title="Activité par heure et jour"
      subtitle="Heure de Paris — échelle proportionnelle, le tableau donne les valeurs exactes"
      className="chart-grid__wide"
    >
      <div className="heatmap">
        {/* Coin vide, puis les etiquettes d'heures, une sur trois pour ne pas
            se chevaucher sur mobile. */}
        <span />
        {HOURS.map((hour) => (
          <span className="heatmap__col-label" key={`h-${hour}`}>
            {hour % 3 === 0 ? hour : ''}
          </span>
        ))}

        {rows.map((row) => (
          <Fragment key={row.weekday}>
            <span className="heatmap__row-label">{row.label}</span>
            {HOURS.map((hour) => {
              const actions = grid.get(`${row.weekday}-${hour}`) ?? 0;
              return (
                <div
                  key={`${row.weekday}-${hour}`}
                  className="heatmap__cell"
                  style={{ background: colorFor(actions) }}
                  // `title` plutot qu'une infobulle maison : natif au survol,
                  // annonce par les lecteurs d'ecran, et zero JavaScript sur
                  // 168 cases.
                  title={`${row.label} ${String(hour).padStart(2, '0')} h — ${formatInteger(actions)} action${actions > 1 ? 's' : ''}`}
                />
              );
            })}
          </Fragment>
        ))}
      </div>

      <div className="heatmap__scale">
        <span>{formatInteger(min)}</span>
        <span className="heatmap__swatch" style={{ background: HEATMAP_EMPTY }} aria-hidden="true" />
        {SEQUENTIAL_RAMP.map((color) => (
          <span className="heatmap__swatch" style={{ background: color }} key={color} aria-hidden="true" />
        ))}
        <span>{formatInteger(max)} actions</span>
      </div>

      <details className="data-toggle">
        <summary>Voir les données</summary>
        <div className="data-toggle__body table-wrap">
          <table className="data">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.numeric ? 'num' : undefined}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.weekday}>
                  {columns.map((column) => (
                    <td key={column.key} className={column.numeric ? 'num' : undefined}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Card>
  );
}
